import * as CL from '@design-system/component-library';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { DeviceSelectionProductTypeCoCo } from '../../../common/enums.js';
import { createEmployeeProductSelection, patchEmployeeProductSelection } from '../../../common/fetch.js';
import { useSearchParams } from '../../../common/hooks/useSearchParams.js';
import {
  descriptionMsg,
  pcsMsg,
  productTypeMsg,
  returnMsg,
  saveMsg,
  selectedDevicesInDeviceSelectionMsg,
  somethingWentWrongMsg,
  startingAtMsg,
  sumPerMonthMsg,
  t,
} from '../../../common/i18n/index.js';
import {
  deviceSelectionTabPath,
  getDeviceSelectionProductTypeLabel,
} from '../../../common/utils/deviceListTabsUtilsCoCo.js';
import {
  getDeviceSelectionCategoryConfig,
  getDeviceSelectionCategoryGroups,
} from '../../../common/utils/deviceSelectionCategoryUtilsCoCo.js';
import {
  getDeviceSelectionPrice,
  getSelectedDeviceCategoryCounts,
} from '../../../common/utils/deviceSelectionDeviceUtilsCoCo.js';
import { getDeviceSelectionPatch } from '../../../common/utils/deviceSelectionEditUtilsCoCo.js';
import { formatSumToString } from '../../../common/utils/priceUtils.js';
import { EmployeeProductType } from '../../../generated/api/employeeProductType.js';
import { addNotification } from '../../../public/site/notificationStore.js';
import { getActiveAccountMasterId } from '../../../selfservice/common/localStorageUtils.js';
import type { DeviceSelectionDeviceCoCo } from '../../../common/utils/deviceSelectionDeviceUtilsCoCo.js';
import type { DeviceSelectionSummaryStateCoCo } from '../../../common/utils/deviceSelectionFormUtilsCoCo.js';
import type { EmployeeProductSelectionResponse } from '../../../generated/api/employeeProductSelectionResponse.js';

import './DeviceSelectionSummaryCoCo.scss';

interface DeviceSelectionSummaryCoCoProps {
  selection: DeviceSelectionSummaryStateCoCo;
  onReturn?: () => void;
  originalSelection?: EmployeeProductSelectionResponse;
  devices: DeviceSelectionDeviceCoCo[];
}

export const DeviceSelectionSummaryCoCo = ({
  selection,
  onReturn,
  originalSelection,
  devices,
}: DeviceSelectionSummaryCoCoProps) => {
  const navigate = useNavigate();
  const { mdmId } = useSearchParams<{ mdmId?: string }>();
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const selectedIds = new Set(selection.selectedDeviceIds);
  const selectedDevices = devices.filter(({ id }) => selectedIds.has(id));
  const categoryGroups = getDeviceSelectionCategoryGroups(selection.productType);
  const unresolvedItems =
    originalSelection?.items.filter(
      item => selectedIds.has(item.variantGroupId) && !devices.some(device => device.id === item.variantGroupId)
    ) ?? [];
  const categoryCounts = getSelectedDeviceCategoryCounts(selection.selectedDeviceIds, selection.productType, devices);
  const visibleCategoryCounts = categoryCounts.filter(({ count }) => count > 0);
  const total = selection.selectedDeviceIds.length;

  const handleSave = async () => {
    if (savingRef.current) {
      return;
    }
    const accountMdmId = mdmId ?? originalSelection?.accountMdmId ?? getActiveAccountMasterId();
    if (!accountMdmId || !selection.productType) {
      addNotification(t.JDTO(somethingWentWrongMsg), 'error');
      return;
    }

    savingRef.current = true;
    setSaving(true);
    try {
      const response = originalSelection
        ? await patchEmployeeProductSelection(
            originalSelection.uuid,
            getDeviceSelectionPatch(originalSelection, selection, devices),
            accountMdmId
          )
        : await createEmployeeProductSelection({
            name: selection.name,
            description: selection.description,
            accountMdmId,
            active: true,
            productType:
              selection.productType === DeviceSelectionProductTypeCoCo.EPP
                ? EmployeeProductType.EPP
                : EmployeeProductType.Purchased_Device,
            items: selectedDevices.map(({ id, name }) => ({ variantGroupId: id, variantGroupName: name })),
          });

      if (response.ok) {
        navigate(`${deviceSelectionTabPath}&${new URLSearchParams({ mdmId: accountMdmId })}`);
      } else {
        addNotification(t.JDTO(somethingWentWrongMsg), 'error');
      }
    } catch {
      addNotification(t.JDTO(somethingWentWrongMsg), 'error');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div className="of-device-selection-summary-coco">
      <dl className="of-device-selection-summary-coco__details">
        <div>
          <dt>{t.VJ0T('Name of device selection')}</dt>
          <dd>{selection.name}</dd>
        </div>
        <div>
          <dt>{t.HBW3(descriptionMsg)}</dt>
          <dd>{selection.description}</dd>
        </div>
        <div>
          <dt>{t.R6HP(productTypeMsg)}</dt>
          <dd>{selection.productType && getDeviceSelectionProductTypeLabel(selection.productType)}</dd>
        </div>
      </dl>

      <section aria-labelledby="device-selection-summary-devices">
        <h3 id="device-selection-summary-devices" className="of-device-selection-summary-coco__section-title">
          {t.MDFW(selectedDevicesInDeviceSelectionMsg)}
        </h3>
        <div className="of-device-selection-summary-coco__groups">
          {categoryGroups.flatMap(group =>
            group.categories
              .map(({ category }) => ({
                category,
                devices: selectedDevices.filter(device => device.category === category),
              }))
              .filter(({ devices: categoryDevices }) => categoryDevices.length > 0)
              .map(({ category, devices: categoryDevices }) => {
                const categoryConfig = getDeviceSelectionCategoryConfig(category);
                return (
                  <div className="of-device-selection-summary-coco__group" key={category}>
                    <h4>
                      {categoryConfig.label()}: {categoryDevices.length} {t.B3MG(pcsMsg)}
                    </h4>
                    <ul>
                      {categoryDevices.map(device => (
                        <li key={device.id}>
                          <span>{device.name}</span>
                          <span>
                            {device.oneTimePrice === undefined
                              ? `${t.OD1L(startingAtMsg)} ${t.YO7F(sumPerMonthMsg, formatSumToString(getDeviceSelectionPrice(device)))}`
                              : formatSumToString(getDeviceSelectionPrice(device))}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })
          )}
          {unresolvedItems.length > 0 && (
            <div className="of-device-selection-summary-coco__group">
              <h4>{t.O9AW('Devices whose category is unavailable')}</h4>
              <ul>
                {unresolvedItems.map(item => (
                  <li key={item.uuid}>{item.variantGroupName}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      <footer className="of-device-selection-footer-coco">
        <div className="of-device-selection-footer-coco__content">
          <div className="of-device-selection-summary-coco__summary">
            <div className="of-device-selection-footer-coco__total">
              {t.MSV2('Selected in total')}: {total} {t.B3MG(pcsMsg)}
            </div>
            {visibleCategoryCounts.length > 0 && (
              <ul className="of-device-selection-footer-coco__categories">
                {visibleCategoryCounts.map(({ category, count }) => (
                  <li key={category}>
                    {getDeviceSelectionCategoryConfig(category).label()}: {count} {t.B3MG(pcsMsg)}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="of-device-selection-summary-coco__actions">
            <CL.Button color="secondary" size="l" onClick={onReturn ?? (() => navigate(-1))} disabled={saving}>
              {t.VH59(returnMsg)}
            </CL.Button>
            <CL.Button color="primary" size="l" onClick={handleSave} disabled={saving} loading={saving}>
              {t.NIWE(saveMsg)}
            </CL.Button>
          </div>
        </div>
      </footer>
    </div>
  );
};
